import React, { useEffect, useState, useContext } from "react";
import "./LoadProfileRouteInfoRow.css";
import { useMsal } from "@azure/msal-react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import DialButton from "../../../Buttons/DialButton/DialButton";
import SecondaryButton from "../../../Buttons/SecondaryButton/SecondaryButton";
import EmailButton from "../../../Buttons/EmailButton/EmailButton";
import PrimaryDateTimePicker from "../../../Inputs/PrimaryDateTimePicker/PrimaryDateTimePicker";
import LoadProfileRouteInfoModal from "../LoadProfileRouteInfoModal/LoadProfileRouteInfoModal";
import { isEqual, isValid, formatISO } from "date-fns";
import { parseAndFormatDate } from "../../../../utils/dateHelpers";
import { formatTitleCase } from "../../../../utils/formatHelpers";
import { LocationService } from "../../../../services/LocationService";
import { AlertContext } from "../../../Dashboard/Dashboard";
import TrashCanIcon from "../../../../assets/icons/TrashCanIcon";

export default function LoadProfileRouteInfoRow({
  stop,
  brokerageStatus,
  bolNumber,
  conNumber,
  stopsUpdated,
  setStopsUpdated,
  handleUpdateStops,
  clearTimeFromStops,
  isLoading
}) {
  const { instance } = useMsal();
  const [schedArriveEarly, setSchedArriveEarly] = useState("");
  const [schedArriveLate, setSchedArriveLate] = useState("");
  const [actualArrival, setActualArrival] = useState(null);
  const [actualDeparture, setActualDeparture] = useState(null);
  const [hoursOperation, setHoursOperation] = useState([]);
  const [referenceNumbers, setReferenceNumbers] = useState([]);
  const [stopNotes, setStopNotes] = useState([]);
  const { warning } = useContext(AlertContext);

  const handleStopsUpdated = stopUpdated => {
    let stops = [...stopsUpdated];

    const index = stops.findIndex(s => s.id === stop.id);
    if (index >= 0) {
      stops[index] = stopUpdated;
    } else {
      stops.push(stopUpdated);
    }

    setStopsUpdated(stops);
  };
  const handleClearTimeFromStop = (selectedStop, clearBothTimes = false) => {
    let clearedStop = { ...selectedStop };
    if (clearBothTimes) {
      clearedStop.actualArrival = null;
      clearedStop.actualDeparture = null;
    } else {
      clearedStop.actualDeparture = null;
    }
    clearTimeFromStops(clearedStop);
  };
  useEffect(() => {
    let stopUpdated = stopsUpdated.find(s => s.id === stop.id) ?? stop;
    stopUpdated = { ...stopUpdated };
    const originalActualArrival = new Date(stop.actualArrival);
    if (isValid(actualArrival) && !isEqual(actualArrival, originalActualArrival)) {
      stopUpdated.actualArrival = formatISO(actualArrival);
      handleStopsUpdated(stopUpdated);
    }

    const originalActualDeparture = new Date(stop.actualDeparture);
    if (isValid(actualDeparture) && !isEqual(actualDeparture, originalActualDeparture)) {
      stopUpdated.actualDeparture = formatISO(actualDeparture);
      handleStopsUpdated(stopUpdated);
    }
  }, [actualArrival, actualDeparture]);

  useEffect(() => {
    setSchedArriveEarly(parseAndFormatDate(stop.schedArriveEarly, "MM/dd/yy HH:mm"));

    if (stop.actualArrival) {
      setActualArrival(new Date(stop.actualArrival));
    }

    if (stop.actualDeparture) {
      setActualDeparture(new Date(stop.actualDeparture));
    }

    if (stop.schedArriveLate) {
      setSchedArriveLate(parseAndFormatDate(stop.schedArriveLate, "MM/dd/yy HH:mm"));
    } else {
      setSchedArriveLate(parseAndFormatDate(stop.schedArriveEarly, "MM/dd/yy HH:mm"));
    }

    if (stop.referenceNumbers) {
      let refNums = [...stop.referenceNumbers];
      if (stop.stopType === "PU") {
        refNums = stop.referenceNumbers.filter(r => r.reference_qual === "PO" || r.reference_qual === "PU");
        if (bolNumber) refNums.push({ reference_qual: "BOL", reference_number: bolNumber });
        setReferenceNumbers(refNums);
      } else if (stop.stopType === "SO") {
        refNums = refNums.filter(r => r.reference_qual === "KK");
        if (conNumber) refNums.push({ reference_qual: "CON", reference_number: conNumber });
        setReferenceNumbers(refNums);
      }
    }

    if (stop.stopNotes) {
      setStopNotes(stop.stopNotes.filter(({ comment_type }) => comment_type === "HC"));
    }

    if (stop.locationId) {
      LocationService.GetLocation(instance, stop.locationId).then(res => {
        let hoursOps = [];

        if (res.mondayOpen && res.mondayClose) hoursOps.push({ id: "monday", day: "M", time: `${parseAndFormatDate(res.mondayOpen, "HH:mm")} - ${parseAndFormatDate(res.mondayClose, "HH:mm")}` });
        if (res.tuesdayOpen && res.tuesdayClose) hoursOps.push({ id: "tuesday", day: "T", time: `${parseAndFormatDate(res.tuesdayOpen, "HH:mm")} - ${parseAndFormatDate(res.tuesdayClose, "HH:mm")}` });
        if (res.wednesdayOpen && res.wednesdayClose) hoursOps.push({ id: "wednesday", day: "W", time: `${parseAndFormatDate(res.wednesdayOpen, "HH:mm")} - ${parseAndFormatDate(res.wednesdayClose, "HH:mm")}` });
        if (res.thursdayOpen && res.thursdayClose) hoursOps.push({ id: "thursday", day: "T", time: `${parseAndFormatDate(res.thursdayOpen, "HH:mm")} - ${parseAndFormatDate(res.thursdayClose, "HH:mm")}` });
        if (res.fridayOpen && res.fridayClose) hoursOps.push({ id: "friday", day: "F", time: `${parseAndFormatDate(res.fridayOpen, "HH:mm")} - ${parseAndFormatDate(res.fridayClose, "HH:mm")}` });
        if (res.saturdayOpen && res.saturdayClose) hoursOps.push({ id: "saturday", day: "S", time: `${parseAndFormatDate(res.saturdayOpen, "HH:mm")} - ${parseAndFormatDate(res.saturdayClose, "HH:mm")}` });
        if (res.sundayOpen && res.sundayClose) hoursOps.push({ id: "sunday", day: "S", time: `${parseAndFormatDate(res.sundayOpen, "HH:mm")} - ${parseAndFormatDate(res.sundayClose, "HH:mm")}` });

        setHoursOperation(hoursOps);
      }).catch(error => {
        console.error(error);
        warning(error);
      });
    }
  }, [stop]);
  const canEditDateTime = () => {
    return brokerageStatus != "AVA" && brokerageStatus != "A-RTGDE" && brokerageStatus != "ONHOLD";
  };
  const showClearTimeButton = (actualStopTime) => {
    return clearTimeFromStops != null && actualStopTime;
  };
  return (
    <Box
      backgroundColor={stop.orderSequence === 1 ? "primary.05" : "black_opacity.05"}
      borderRadius="4px"
    >
      <Box display="flex" padding="12px">
        <Typography variant="h1" color="black_opacity.100">
          {`${formatTitleCase(stop.cityName)}, ${stop.state}`}
        </Typography>
        <Box marginLeft="auto">
          <LoadProfileRouteInfoModal
            city={stop.cityName}
            state={stop.state}
            hoursOperation={hoursOperation}
            referenceNumbers={referenceNumbers}
            stopNotes={stopNotes}
          />
        </Box>
      </Box>
      <Grid container spacing={2} sx={{ padding: "12px" }}>
        <Grid item xs>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <Box className="Load-route-info-container">
                <Typography
                  variant="subhead1"
                  color="black_opacity.100"
                >
                  Scheduled Arrival Window
                </Typography>
                <span className="Load-route-info-value">
                  <Typography
                    variant="body1"
                    color="black_opacity.100"
                  >
                    {schedArriveEarly} - {schedArriveLate}
                  </Typography>
                </span>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Box className="Load-route-info-container">
                <Typography
                  variant="subhead1"
                  color="black_opacity.100"
                >
                  {stop.stopType === "PU" && "Shipper"}
                  {stop.stopType === "SO" && "Receiver"}
                </Typography>
                <span className="Load-route-info-value">
                  <Typography
                    variant="body1"
                    color="black_opacity.100"
                  >
                    {stop.locationName}
                  </Typography>
                </span>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Box className="Load-route-info-container" sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  variant="subhead1"
                  color="black_opacity.100"
                  marginRight="10px"
                >
                  Actual Arrival
                </Typography>
                <span className="Load-route-info-value">
                  {canEditDateTime() ?
                    <Box sx={{ display: "flex" }}>
                      <PrimaryDateTimePicker
                        value={actualArrival}
                        handleChange={setActualArrival}
                        onBlur={() => handleUpdateStops()}
                        error={!isValid(actualArrival)}
                        ampm={false}
                        helperText={!actualArrival ? "Record missing information" : ""}
                      />
                      {showClearTimeButton(stop.actualArrival) ?
                        <Box marginRight="10px">
                          <SecondaryButton
                            onClick={() => handleClearTimeFromStop(stop, true)}
                            startIcon={TrashCanIcon}
                            buttonColor="blue"
                            isLoading={isLoading}
                          />
                        </Box> : <></>
                      }
                    </Box>
                    :
                    <Typography
                      variant="body1"
                      color="black_opacity.100"
                    >
                      {parseAndFormatDate(actualArrival, "MM/dd/yy HH:mm", "-")}
                    </Typography>
                  }
                </span>
              </Box>
              <Box className="Load-route-info-container" sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  variant="subhead1"
                  color="black_opacity.100"
                  marginRight="10px"
                >
                  Actual Departure
                </Typography>
                <span className="Load-route-info-value">
                  {canEditDateTime() ?
                    <Box sx={{ display: "flex" }}>
                      <PrimaryDateTimePicker
                        value={actualDeparture}
                        handleChange={setActualDeparture}
                        onBlur={() => handleUpdateStops()}
                        error={!isValid(actualDeparture)}
                        ampm={false}
                        helperText={!actualDeparture ? "Record missing information" : ""}
                      />

                      {showClearTimeButton(stop.actualDeparture) ?
                        <Box>
                          <SecondaryButton
                            onClick={() => handleClearTimeFromStop(stop, false)}
                            startIcon={TrashCanIcon}
                            buttonColor="blue"
                            isLoading={isLoading}
                          />
                        </Box> : <></>
                      }
                    </Box>
                    :
                    <Typography
                      variant="body1"
                      color="black_opacity.100"
                    >
                      {parseAndFormatDate(actualDeparture, "MM/dd/yy HH:mm", "-")}
                    </Typography>
                  }
                </span>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Box className="Load-route-info-container">
                <Typography
                  variant="subhead1"
                  color="black_opacity.100"
                >
                  Address
                </Typography>
                <span className="Load-route-info-value">
                  <Typography
                    variant="body1"
                    color="black_opacity.100"
                  >
                    {stop.address}
                  </Typography>
                </span>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs>
          <Grid container>
            <Grid item xs={12} sm={6} md={6}>
              <Box className="Load-route-info-container">
                <Typography
                  variant="subhead1"
                  color="black_opacity.100"
                >
                  Reference Numbers
                </Typography>
                <Box className="Load-route-info-value">
                  {referenceNumbers.length ? referenceNumbers.map((r, i) => (i < 3 &&
                    <Box key={`reference-number-${r.id}`}>
                      <Box display="inline-flex">
                        <Typography
                          variant="subhead1"
                          color="black_opacity.100"
                        >
                          {r.reference_qual}:
                        </Typography>
                        <span>
                          <Typography
                            variant="body1"
                            color="black_opacity.100"
                            marginLeft="5px"
                          >
                            {r.reference_number}
                          </Typography>
                        </span>
                      </Box>
                      <br />
                    </Box>)) : "-"
                  }
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Box className="Load-route-info-container">
                <Typography
                  variant="subhead1"
                  color="black_opacity.100"
                >
                  Rate Con Comments
                </Typography>
                <span className="Load-route-info-value">
                  <Typography
                    variant="body1"
                    color="black_opacity.100"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: "4",
                      WebkitBoxOrient: "vertical",
                    }}>
                    {stopNotes.length > 0 ? stopNotes.map(({ comments }) => comments).join(", ") : "-"}
                  </Typography>
                </span>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={{ margin: "12px" }} />
      <Box sx={{ display: "flex", alignItems: "center", padding: "12px" }} >
        <Typography variant="body1" color="black_opacity.100">
          {stop.contactName}
        </Typography>
        <Box marginX="10px">
          <DialButton
            number={stop.phone}
            icon="phone"
            disabled={!stop.phone}
            color="blue"
          />
        </Box>
        <EmailButton email={stop.email} disabled={!stop.email} color="blue" />
      </Box>
    </Box>
  );
}

LoadProfileRouteInfoRow.propTypes = {
  stop: PropTypes.object.isRequired,
  brokerageStatus: PropTypes.string,
  bolNumber: PropTypes.string,
  conNumber: PropTypes.string,
  stopsUpdated: PropTypes.array.isRequired,
  setStopsUpdated: PropTypes.func.isRequired,
  handleUpdateStops: PropTypes.func,
  isLoading: PropTypes.bool,
  clearTimeFromStops: PropTypes.func
};